import { apiService } from './apiService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.eternal-health.com/api';

export type SleepStageType = 'AWAKE' | 'LIGHT' | 'DEEP' | 'REM' | 'UNSPECIFIED';
export type SleepKind = 'MAIN' | 'NAP';

export interface SleepStage {
  id: number;
  stage_type: SleepStageType;
  start_time: string;
  end_time: string;
  duration_minutes: number;
}

export interface SleepSession {
  id: number;
  session_date: string;
  start_time: string;
  end_time: string;
  timezone: string;
  efficiency: number | null;
  waso_minutes: number | null;
  latency_minutes: number | null;
  source_device: string | null;
  source_app: string | null;
  external_id: string | null;
  kind: SleepKind;
  created_at: string;
  updated_at: string;
  stages: SleepStage[];
}

export interface SleepSessionsResponse {
  count: number;
  perpage: number;
  actualPage: number;
  totalPages: number;
  results: SleepSession[];
}

export interface SleepSessionsFilters {
  start_date?: string;
  end_date?: string;
  kind?: SleepKind;
  page?: number;
  perpage?: number;
}

const formatDate = (date: Date): string => date.toISOString().split('T')[0];

const getDateNDaysAgo = (n: number): string => {
  const date = new Date();
  date.setDate(date.getDate() - n);
  return formatDate(date);
};

export const sleepService = {
  getSleepSessions: async (
    userId: string | number,
    filters?: SleepSessionsFilters
  ): Promise<SleepSessionsResponse> => {
    const params = new URLSearchParams();
    const startDate = filters?.start_date ?? getDateNDaysAgo(30);
    const endDate = filters?.end_date ?? formatDate(new Date());
    params.append('start_date', startDate);
    params.append('end_date', endDate);
    if (filters?.kind) params.append('kind', filters.kind);
    params.append('page', String(filters?.page ?? 1));
    params.append('perpage', String(filters?.perpage ?? 500));

    const url = `${API_BASE_URL}/clinic/patient/${userId}/sleep-sessions/?${params.toString()}`;
    return await apiService.get(url);
  },

  getSleepStages: async (
    userId: string | number,
    sessionId: string | number
  ): Promise<SleepStage[]> => {
    const url = `${API_BASE_URL}/clinic/patient/${userId}/sleep-sessions/${sessionId}/stages/`;
    return await apiService.get(url);
  },
};

export const STAGE_COLORS: Record<SleepStageType, string> = {
  DEEP: '#1e3a8a',
  REM: '#8b5cf6',
  LIGHT: '#60a5fa',
  AWAKE: '#f87171',
  UNSPECIFIED: '#cbd5e1',
};

export const STAGE_LABELS: Record<SleepStageType, string> = {
  DEEP: 'Profundo',
  REM: 'REM',
  LIGHT: 'Ligero',
  AWAKE: 'Despierto',
  UNSPECIFIED: 'Sin clasificar',
};
